import Api from './api';

// ========== Catalogs ==========
export const getPeriodicities = () => {
    return Api.get('/periodicities');
};

// ========== Manual provisioning step actions ==========
// Permite executar/reverter steps idempotentes (Seed, WelcomeEmail) em ambientes
// criados manualmente fora do pipeline padrao.
export const getManualStepsWhitelist = () => {
    return Api.get('/admin/provisioning/manual-steps/whitelist');
};

export const executeManualStep = (hirerId, stepName, options = {}) => {
    return Api.post(`/admin/provisioning/hirers/${hirerId}/manual-steps/${stepName}/execute`, {
        resetPassword: options.resetPassword === true,
    });
};

export const rollbackManualStep = (hirerId, stepName, twoFactorCode) => {
    return Api.post(`/admin/provisioning/hirers/${hirerId}/manual-steps/${stepName}/rollback`, { twoFactorCode });
};

// Envia codigo 2FA por email pra rollback de step manual (purpose=provisioning_step_rollback).
export const sendStepRollbackTwoFactorCode = (hirerId, email) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/send-step-rollback-2fa-code`, { email });
};

export const getCompanySizes = () => {
    return Api.get('/admin/billing/company-sizes');
};

export const updateHirerCompanySize = (hirerId, companySizeId) => {
    return Api.put(`/admin/billing/hirers/${hirerId}/company-size`, { companySizeId });
};

// ========== Trials ==========
export const getTrials = (limit = 50, offset = 0, search = '', statusFilter = '') => {
    let url = `/admin/trials?limit=${limit}&offset=${offset}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (statusFilter) url += `&statusFilter=${statusFilter}`;
    return Api.get(url);
};

export const getTrialDetail = (hirerId) => {
    return Api.get(`/admin/trials/${hirerId}`);
};

export const getDeprovisionedTrialDetail = (desprovisioningQueueId) => {
    return Api.get(`/admin/trials/deprovisioned/${desprovisioningQueueId}`);
};

export const createContract = (data) => {
    return Api.post('/admin/contract', data);
};

export const convertTrial = (hirerId, data) => {
    return Api.post(`/admin/trials/${hirerId}/convert`, data);
};

export const cancelTrial = (hirerId) => {
    return Api.delete(`/admin/trials/${hirerId}`);
};

export const reactivateTrial = (hirerId) => {
    return Api.post(`/admin/trials/${hirerId}/reactivate`);
};

// ========== Deprovisioning (trial + subscription) ==========
export const scheduleTrialDeprovisioning = (hirerId, contractId, payload) => {
    return Api.post(`/admin/trials/${hirerId}/contracts/${contractId}/schedule-deprovisioning`, payload);
};

export const abortTrialDeprovisioning = (hirerId, contractId) => {
    return Api.post(`/admin/trials/${hirerId}/contracts/${contractId}/abort-deprovisioning`);
};

export const scheduleSubscriptionDeprovisioning = (hirerId, contractId, payload) => {
    return Api.post(`/admin/subscription/${hirerId}/contracts/${contractId}/schedule-deprovisioning`, payload);
};

export const abortSubscriptionDeprovisioning = (hirerId, contractId) => {
    return Api.post(`/admin/subscription/${hirerId}/contracts/${contractId}/abort-deprovisioning`);
};

export const abortDatabaseDrop = (hirerId, contractId) => {
    return Api.post(`/admin/subscription/${hirerId}/contracts/${contractId}/abort-database-drop`);
};

export const cancelGatewaySubscription = (hirerId, contractId) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/contracts/${contractId}/cancel-gateway-subscription`);
};

export const getDeprovisioningStatus = (hirerId, contractId) => {
    return Api.get(`/admin/billing/hirers/${hirerId}/contracts/${contractId}/deprovisioning-status`);
};

export const getSuspendedPendingDecision = (limit = 50, offset = 0) => {
    return Api.get(`/admin/billing/suspended-pending-decision?limit=${limit}&offset=${offset}`);
};

export const markSuspensionReviewed = (hirerId) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/mark-suspension-reviewed`);
};

// ========== Force Database Drop (2FA) ==========
// Nota: send-force-drop-2fa-code permanece SEM contractId (escopada por hirer).
export const sendForceDropTwoFactorCode = (hirerId, email) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/send-force-drop-2fa-code`, { email });
};

// ========== Schedule Deprovisioning (2FA) ==========
// Purpose distinto do force-drop — código não é intercambiável entre operações.
// Usado em modal "Desprovisionar ambiente" (assinatura) e "Desprovisionar trial".
export const sendScheduleDeprovTwoFactorCode = (hirerId, email) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/send-schedule-deprov-2fa-code`, { email });
};

export const forceDatabaseDrop = (hirerId, contractId, payload) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/contracts/${contractId}/force-database-drop`, payload);
};

export const getContractStatusHistory = (hirerId, limit = 100, offset = 0) => {
    return Api.get(`/admin/billing/hirers/${hirerId}/status-history?limit=${limit}&offset=${offset}`);
};

export const getTrialHirerConfig = (hirerId, workspace = null) => {
    // workspace opcional: tb_hirer_config pode ter multiplas linhas por hirer
    // (DNS/subdomains distintos). Quando passado, backend filtra a linha exata.
    const qs = workspace ? `?workspace=${encodeURIComponent(workspace)}` : '';
    return Api.get(`/admin/trials/${hirerId}/hirer-config${qs}`);
};

export const getUrlSnapshots = (hirerId) => {
    return Api.get(`/admin/trials/${hirerId}/url-snapshots`);
};

export const restoreUrlSnapshot = (hirerId, snapshotId) => {
    return Api.post(`/admin/trials/${hirerId}/restore-url-snapshot/${snapshotId}`);
};

export const updateUrlsToDedicated = (hirerId) => {
    return Api.post(`/admin/trials/${hirerId}/update-urls-dedicated`);
};

export const getTrialProvisioning = (hirerId) => {
    return Api.get(`/admin/trials/${hirerId}/provisioning`);
};

export const updateTrial = (hirerId, data) => {
    return Api.put(`/admin/trials/${hirerId}`, data);
};

// ========== Billing / Hirers ==========
export const getDeprovisioningQueueStats = (contractType = '') => {
    let url = '/admin/billing/deprovisioning-queue/stats';
    if (contractType) url += `?contractType=${contractType}`;
    return Api.get(url);
};

export const dispatchMaintenanceJob = (jobName, hirerId, force = false) => {
    const url = `/admin/billing/maintenance/dispatch-job/${jobName}?hirerId=${hirerId}&force=${force ? 'true' : 'false'}`;
    return Api.post(url);
};

export const getHirers = (limit = 50, offset = 0, search = '', statusFilter = '') => {
    let url = `/admin/billing/hirers?limit=${limit}&offset=${offset}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (statusFilter) url += `&statusFilter=${statusFilter}`;
    return Api.get(url);
};

export const getHirerDetail = (hirerId) => {
    return Api.get(`/admin/billing/hirers/${hirerId}`);
};

export const blockHirer = (hirerId, reason) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/block`, { reason });
};

export const unblockHirer = (hirerId) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/unblock`);
};

export const getHirerInvoices = (hirerId, limit = 20, offset = 0) => {
    return Api.get(`/admin/billing/hirers/${hirerId}/invoices?limit=${limit}&offset=${offset}`);
};

export const getHirerPayments = (hirerId, limit = 20, offset = 0) => {
    return Api.get(`/admin/billing/hirers/${hirerId}/payments?limit=${limit}&offset=${offset}`);
};

export const chargeSubscription = (hirerId, contractId) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/contracts/${contractId}/charge-subscription`);
};

export const syncPayments = (hirerId) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/sync-payments`);
};

export const voidPayment = (hirerId, paymentId) => {
    return Api.delete(`/admin/billing/hirers/${hirerId}/payments/${paymentId}`);
};

export const getHirerHistory = (hirerId, limit = 50, offset = 0) => {
    return Api.get(`/admin/billing/hirers/${hirerId}/history?limit=${limit}&offset=${offset}`);
};

// ========== Failed Payments ==========
export const getFailedPayments = (limit = 50, offset = 0) => {
    return Api.get(`/admin/billing/payments/failed?limit=${limit}&offset=${offset}`);
};

export const retryPayment = (paymentId) => {
    return Api.post(`/admin/billing/payments/${paymentId}/retry`);
};

// ========== Subscription Conversions ==========
export const convertToDedicated = (hirerId, products) => {
    return Api.post('/admin/subscription/convert-dedicated', { hirerId, products });
};

export const convertToTrial = (hirerId, trialDurationDays = 14) => {
    return Api.post('/admin/subscription/convert-trial', { hirerId, trialDurationDays });
};

export const convertToShared = (hirerId, products) => {
    return Api.post('/admin/subscription/convert-shared', { hirerId, products });
};

export const switchProductPlan = (hirerId, applicationId, newPlanPeriodicityId) => {
    return Api.post('/admin/subscription/switch-product-plan', { hirerId, applicationId, newPlanPeriodicityId });
};

// ========== Provisioning ==========
export const getProvisioningQueue = (limit = 50, offset = 0, statusFilter = '', modeFilter = '') => {
    let url = `/admin/provisioning/queue?limit=${limit}&offset=${offset}`;
    if (statusFilter) url += `&statusFilter=${statusFilter}`;
    if (modeFilter) url += `&modeFilter=${modeFilter}`;
    return Api.get(url);
};

export const getProvisioningDetail = (id) => {
    return Api.get(`/admin/provisioning/${id}`);
};

export const retryProvisioning = (id) => {
    return Api.post(`/admin/provisioning/retry/${id}`);
};

export const continueProvisioning = (id) => {
    return Api.post(`/admin/provisioning/continue/${id}`);
};

export const resetProvisioningStatus = (id) => {
    return Api.post(`/admin/provisioning/${id}/reset-status`);
};

export const retryDeprovisioning = (id) => {
    return Api.post(`/admin/provisioning/${id}/retry-deprovisioning`);
};

// Force-cleanup K8s pra contrato JÁ desprovisionado cujo teardown deixou recursos órfãos
// no cluster. Retorna 202 Accepted; o trabalho roda assíncrono no handler RabbitMQ.
// Polling do detail panel (history) renderiza o progresso via linhas force_cleanup_k8s_*.
export const forceK8sCleanup = (id) => {
    return Api.post(`/admin/provisioning/${id}/force-k8s-cleanup`);
};

export const syncProvisioningStatus = (id) => {
    return Api.post(`/admin/provisioning/${id}/sync-status`);
};

// Catálogo dinâmico dos steps de provisionamento (order, stepName, description, dedicatedOnly).
// Fonte de verdade no backend (ProvisioningStepsCatalog) — frontend não mantém mais hardcoded.
export const getProvisioningStepsCatalog = () => {
    return Api.get('/admin/provisioning/steps-catalog');
};

export const executeProvisioningStep = (id, stepOrder) => {
    return Api.post(`/admin/provisioning/${id}/execute-step/${stepOrder}`);
};

export const rollbackProvisioningStep = (id, stepOrder) => {
    return Api.post(`/admin/provisioning/${id}/rollback-step/${stepOrder}`);
};

export const reportProvisioningStepError = (id, stepOrder) => {
    return Api.post(`/admin/provisioning/${id}/report-error/${stepOrder}`);
};

// ========== Stripe Subscription ==========
export const createStripeSubscription = (hirerId, contractId, planPeriodicityId) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/contracts/${contractId}/create-subscription`, { planPeriodicityId });
};

// ========== Contract Management ==========
export const updateContractStatus = (hirerId, contractId, contractStatusId, changedBy = 'admin') => {
    return Api.put(`/admin/billing/hirers/${hirerId}/contracts/${contractId}/status`, { contractStatusId, changedBy });
};

export const updateContractPeriodEnd = (hirerId, contractId, periodEnd) => {
    return Api.put(`/admin/billing/hirers/${hirerId}/contracts/${contractId}/period-end`, { periodEnd });
};

export const updateContractLimits = (hirerId, contractId, limits) => {
    return Api.put(`/admin/billing/hirers/${hirerId}/contracts/${contractId}/limits`, limits);
};

export const updateProductLimits = (contractPlanPeriodicityId, data) => {
    return Api.put(`/admin/billing/products/${contractPlanPeriodicityId}/limits`, data);
};

// ========== Contract Products (Multi-Product) ==========
export const getContractProducts = (hirerId) => {
    return Api.get(`/admin/billing/hirers/${hirerId}/products`);
};

export const addProductToContract = (hirerId, data) => {
    return Api.post(`/admin/billing/hirers/${hirerId}/products`, data);
};

export const removeProductFromContract = (hirerId, applicationId) => {
    return Api.delete(`/admin/billing/hirers/${hirerId}/products/${applicationId}`);
};

// ========== Credits ==========
export const getCreditBalances = (hirerId) => {
    return Api.get(`/credits/balances/${hirerId}`);
};

export const getCreditTransactions = (hirerId, creditBalanceId = null, limit = 50, offset = 0) => {
    let url = `/credits/transactions/${hirerId}?limit=${limit}&offset=${offset}`;
    if (creditBalanceId) url += `&creditBalanceId=${creditBalanceId}`;
    return Api.get(url);
};

export const purchaseCredits = (hirerId, contractPlanPeriodicityId, amount, paymentMethodId) => {
    return Api.post('/credits/purchase', { hirerId, contractPlanPeriodicityId, amount, paymentMethodId });
};

export const getCreditPaymentMethods = (contractPlanPeriodicityId) => {
    return Api.get(`/credits/payment-methods/${contractPlanPeriodicityId}`);
};

export const updateCreditSettings = (hirerId, contractPlanPeriodicityId, alertThreshold, autoRechargeEnabled, autoRechargeAmount, autoRechargeTrigger) => {
    return Api.post('/credits/settings', { hirerId, contractPlanPeriodicityId, alertThreshold, autoRechargeEnabled, autoRechargeAmount, autoRechargeTrigger });
};

// ========== Usage Metering ==========
export const getHirerUsage = (hirerId, period = null) => {
    let url = `/admin/billing/hirers/${hirerId}/usage`;
    if (period) url += `?period=${encodeURIComponent(period)}`;
    return Api.get(url);
};

// ========== Payment Methods (admin view) ==========
export const getHirerPaymentMethods = (hirerId) => {
    return Api.get(`/admin/billing/hirers/${hirerId}/payment-methods`);
};

// ========== Payment Gateways ==========
export const getPaymentGateways = () => {
    return Api.get('/admin/billing/payment-gateways');
};

export const updateContractGateway = (hirerId, contractId, paymentGatewayId) => {
    return Api.put(`/admin/billing/hirers/${hirerId}/contracts/${contractId}/gateway`, { paymentGatewayId });
};

// ========== Auxiliary ==========
export const getActivePlans = (includeTrial = false) => {
    return Api.get(`/admin/trials/plans?includeTrial=${includeTrial}`);
};

export const validateWorkspace = (workspace) => {
    return Api.get(`/signup/validate-workspace?workspace=${encodeURIComponent(workspace)}`);
};
